import {v4 as uuidv4} from 'uuid';
import {getItemAltText} from '../../accessibility/getItemAltText';
import {getEnv} from '../../common/env/env';
import {getCommonBadge} from '../../common/getCommonBadge';
import {getNormalListViewMetaList} from '../../common/getNormalListViewMetaList';
import {getSubtitleList} from '../../common/getSubtitleList';
import {t} from '../../common/getTranslations';
import {ItemSeriesDto} from '../../common/shared/api/gateway/ItemSeriesDto.interface';
import {ItemSetDto} from '../../common/shared/api/gateway/ItemSetDto.interface';
import {ItemSingleDto} from '../../common/shared/api/gateway/ItemSingleDto.interface';
import {CategoryTypeVo} from '../../common/shared/api/nicolas/vo/enums/CategoryTypeVo.enum';
import {SlideTypeVo} from '../../common/shared/api/nicolas/vo/enums/SlideTypeVo.enum';
import {HelixTodayGiftDto} from '../../common/shared/api/nicolas/vo/HelixTodayGiftDto.interface';
import {SearchSetItemDto} from '../../common/shared/api/nicolas/vo/SearchItemDto.interface';
import {TodayGiftDto} from '../../common/shared/api/nicolas/vo/TodayGiftDto.interface';
import {formatWithCommas} from '../../common/shared/utils/formatWithCommas';
import {getDiscountRateText} from '../../common/shared/utils/getDiscountRateText';
import {getFormattedNumberWithSuffix} from '../../common/shared/utils/getFormattedNumberWithSuffix';
import {getKageUrl} from '../../common/shared/utils/getKageUrl';
import {getTicketExpiredText} from '../../common/shared/utils/getTicketExpiredText';
import {parseDate} from '../../common/shared/utils/parseDate';
import {removeCategoryName} from '../../common/shared/utils/removeCategoryName';
import {timeago} from '../../date/timeago';
import {
  ContinueProductType,
  EventLog,
  ItemType,
  Maybe,
  MetaInfoType,
  NormalListViewItem,
  SaleState,
  TicketKind,
  User,
} from '../generated/types';
import {createAgeGrade, getAgeGradeVo} from './AgeGrade';
import {createContinueInfo} from './ContinueInfo';
import {createHelixTodayGift} from './HelixTodayGift';
import {createSaleState} from './SaleState';
import {createSlideType} from './SlideType.enum';
import {createTicketKind} from './TicketKind';
import {createTodayGift} from './TodayGift';

export function createNormalListViewItem(
  userInfo: Maybe<User>,
  param:
    | {
        sectionLayoutItemSeriesDto: ItemSeriesDto;
      }
    | {
        contentHomeSameItemSeriesDto: ItemSeriesDto;
      }
    | {
        contentSetDto: ItemSetDto;
      }
    | {
        listViewLandingItemSeriesDto: ItemSeriesDto;
      }
    | {
        todayGiftItemSeriesDto: ItemSeriesDto;
      }
    | {
        invenLikeItemSeriesDto: ItemSeriesDto;
      }
    | {
        invenPaidItemSeriesDto: ItemSeriesDto;
      }
    | {
        invenRecentItemSeriesDto: ItemSeriesDto;
      }
    | {
        searchThemeKeywordItemSeriesDto: ItemSeriesDto;
      }
    | {
        searchCommonItemSeriesDto: ItemSeriesDto;
        shouldShowCategory: boolean;
      }
    | {
        searchCommonItemSetDto: SearchSetItemDto;
        shouldShowCategory: boolean;
      }
    | {
        listViewLandingItemSingleDto: ItemSingleDto;
      }
    | {
        listViewLandingItemSetDto: ItemSetDto;
      }
): NormalListViewItem {
  if ('sectionLayoutItemSeriesDto' in param) {
    return withSectionLayoutItemSeriesDto(
      userInfo,
      param.sectionLayoutItemSeriesDto
    );
  } else if ('contentHomeSameItemSeriesDto' in param) {
    return withContentHomeSameItemSeriesDto(
      userInfo,
      param.contentHomeSameItemSeriesDto
    );
  } else if ('contentSetDto' in param) {
    return withContentSetDto(param.contentSetDto);
  } else if ('listViewLandingItemSeriesDto' in param) {
    return withListViewLandingItemSeriesDto(
      userInfo,
      param.listViewLandingItemSeriesDto
    );
  } else if ('todayGiftItemSeriesDto' in param) {
    return withTodayGiftItemSeriesDto(userInfo, param.todayGiftItemSeriesDto);
  } else if ('invenLikeItemSeriesDto' in param) {
    return withInvenLikeSeriesDto(userInfo, param.invenLikeItemSeriesDto);
  } else if ('invenPaidItemSeriesDto' in param) {
    return withInvenPaidSeriesDto(userInfo, param.invenPaidItemSeriesDto);
  } else if ('invenRecentItemSeriesDto' in param) {
    return withInvenRecentSeriesDto(userInfo, param.invenRecentItemSeriesDto);
  } else if ('searchThemeKeywordItemSeriesDto' in param) {
    return withSearchThemeKeywordItemSeriesDto(
      userInfo,
      param.searchThemeKeywordItemSeriesDto
    );
  } else if ('searchCommonItemSeriesDto' in param) {
    return withSearchCommonItemSeriesDto(
      userInfo,
      param.searchCommonItemSeriesDto,
      param.shouldShowCategory
    );
  } else if ('searchCommonItemSetDto' in param) {
    return withSearchCommonItemSetDto(
      param.searchCommonItemSetDto,
      param.shouldShowCategory
    );
  } else if ('listViewLandingItemSingleDto' in param) {
    return withListViewLandingItemSingleDto(
      userInfo,
      param.listViewLandingItemSingleDto
    );
  } else if ('listViewLandingItemSetDto' in param) {
    return withListViewLandingItemSetDto(
      userInfo,
      param.listViewLandingItemSetDto
    );
  } else {
    throw new Error('지원하지 않는 타입입니다');
  }
}

function withSectionLayoutItemSeriesDto(
  userInfo: Maybe<User>,
  {
    title,
    thumbnail,
    category,
    series_id,
    category_uid,
    sub_category,
    authors,
    last_slide_added_dt,
    on_issue,
    service_property,
    age_grade,
    badge,
    is_waitfree,
    waitfree_period_by_minute,
    business_model,
    operator_property,
  }: ItemSeriesDto
): NormalListViewItem {
  const currentDate = new Date();
  const {title: operatorTitle, self_censorship} = operator_property || {};
  const {badgeList, ageGradeBadge, statusBadge, statusLabelText} =
    getCommonBadge({
      itemType: ItemType.NormalListView,
      badge,
      age_grade,
      is_waitfree,
      waitfree_period_by_minute: waitfree_period_by_minute || null,
      business_model,
      self_censorship,
      is_manga: operator_property?.is_manga,
    });
  const continueData = service_property?.page_continue
    ? createContinueInfo(service_property?.page_continue, series_id)
    : undefined;
  return {
    id: `NormalList-SectionLayoutItemSeriesDto-${series_id}`,
    isChecked: false,
    isCheckMode: false,
    showPlayerIcon: false,
    type: ItemType.NormalListView,
    badgeList,
    ageGradeBadge,
    statusBadge,
    statusLabelText,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    row1: operatorTitle || title, // 동일작의 경우 운영툴에서 설정한 타이틀을 우선으로 보여줍니다
    row2: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: category,
      subCategoryName:
        category !== sub_category
          ? removeCategoryName(sub_category, category_uid)
          : undefined,
      servicePropertyDto: undefined,
      authors,
    }),
    row3: {
      id: uuidv4(),
      metaList: getNormalListViewMetaList({
        viewCount: service_property?.view_count,
        lastSlideAddedDt: last_slide_added_dt,
        onIssueVo: on_issue,
        currentDate,
      }),
    },
    row5: getTicketExpiredText(service_property?.today_gift?.ticket_expire_at), // TODO: 선물 이용권 로직 추가
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    continueData,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

function withContentHomeSameItemSeriesDto(
  userInfo: Maybe<User>,
  {
    title,
    thumbnail,
    category,
    series_id,
    sub_category,
    age_grade,
    badge,
    is_waitfree,
    waitfree_period_by_minute,
    business_model,
    operator_property,
  }: ItemSeriesDto
): NormalListViewItem {
  const {title: operatorTitle, self_censorship} = operator_property || {};
  const {badgeList, ageGradeBadge, statusBadge, statusLabelText} =
    getCommonBadge({
      itemType: ItemType.NormalListView,
      badge,
      age_grade,
      is_waitfree,
      waitfree_period_by_minute: waitfree_period_by_minute || null,
      business_model,
      self_censorship,
      is_manga: operator_property?.is_manga,
    });
  const eventLog: EventLog = {
    eventMeta: {
      id: String(series_id),
      type: 'seriesId',
      provider: '서비스',
      name: title,
      series: title,
      series_id: String(series_id),
      category,
      subcategory: sub_category,
    },
  };
  return {
    id: `NormalList-ContentHomeSameItemSeriesDto-${series_id}`,
    isChecked: false,
    isCheckMode: false,
    showPlayerIcon: false,
    type: ItemType.NormalListView,
    badgeList,
    ageGradeBadge,
    statusBadge,
    statusLabelText,
    eventLog,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    row1: operatorTitle || title, // 동일작의 경우 운영툴에서 설정한 타이틀을 우선으로 보여줍니다
    row2: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.Category,
      categoryName: category,
      servicePropertyDto: undefined,
    }),
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

function withContentSetDto({
  set_id,
  title,
  thumbnail,
  age_grade,
  price,
  discount_price,
  discount_rate,
  operator_property,
}: ItemSetDto): NormalListViewItem {
  const ageGrade = createAgeGrade(age_grade);
  const {ageGradeBadge} = getCommonBadge({
    itemType: ItemType.NormalListView,
    is_waitfree: false,
    age_grade,
    waitfree_period_by_minute: null,
    self_censorship: operator_property?.self_censorship,
  });

  return {
    id: String(set_id),
    type: ItemType.NormalListView,
    isCheckMode: false,
    isChecked: false,
    showPlayerIcon: false,
    badgeList: [],
    ageGrade,
    thumbnail: getKageUrl({
      kageUrlPrefix: getEnv().kageUrlPrefix,
      kageId: thumbnail,
      resolution: 'th3',
    }),
    row1: title,
    price,
    discountPrice: discount_price,
    discountRate: discount_rate,
    discountRateText: discount_rate ? `${discount_rate}%` : undefined,
    ageGradeBadge,
  };
}

function withListViewLandingItemSeriesDto(
  userInfo: Maybe<User>,
  {
    title,
    thumbnail,
    category,
    series_id,
    sub_category,
    age_grade,
    authors,
    category_uid,
    last_slide_added_dt,
    pub_period,
    on_issue,
    service_property,
    operator_property,
    badge,
    business_model,
    is_waitfree,
    waitfree_period_by_minute,
  }: ItemSeriesDto
): NormalListViewItem {
  const currentDate = new Date();
  const benefitInfoText = getBenefitInfoText(service_property?.today_gift);
  const {badgeList, ageGradeBadge, statusBadge, statusLabelText} =
    getCommonBadge({
      itemType: ItemType.NormalListView,
      badge,
      age_grade,
      is_waitfree,
      waitfree_period_by_minute: waitfree_period_by_minute || null,
      business_model,
      self_censorship: operator_property?.self_censorship,
      is_manga: operator_property?.is_manga,
    });

  const helixImpId = operator_property?.helix_imp_id;

  return {
    id: `NormalList-listViewLandingItemSeriesDto-${series_id}`,
    isChecked: false,
    isCheckMode: false,
    showPlayerIcon: false,
    type: ItemType.NormalListView,
    badgeList,
    ageGradeBadge,
    statusBadge,
    statusLabelText,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    row1: title,
    row2: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: category,
      subCategoryName:
        category !== sub_category
          ? removeCategoryName(sub_category, category_uid)
          : undefined,
      servicePropertyDto: undefined,
      authors,
    }),
    row3: {
      id: uuidv4(),
      metaList: getNormalListViewMetaList({
        viewCount: service_property?.view_count,
        lastSlideAddedDt: last_slide_added_dt,
        onIssueVo: on_issue,
        currentDate,
        pubPeriod: pub_period,
      }),
    },
    row4: benefitInfoText ? [benefitInfoText] : undefined,
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    eventLog: {
      eventMeta: {
        id: String(series_id),
        type: 'seriesId',
        provider: helixImpId ? 'helix' : '서비스',
        name: title,
        series: title,
        series_id: String(series_id),
        category,
        subcategory: sub_category,
      },
      customProps: {
        landing_path: 'contents', // 작품홈 랜딩
        view_type: 'series_list_view',
        ...(helixImpId
          ? {
              helix_id: helixImpId,
              helix_yn: 'y',
            }
          : {}),
      },
      viewimp_contents: helixImpId
        ? {
            imp_provider: 'helix',
            imp_type: 'vimp',
            id: String(series_id),
            type: '작품홈',
            name: title,
          }
        : undefined,
    },
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

function withTodayGiftItemSeriesDto(
  userInfo: Maybe<User>,
  {
    title,
    thumbnail,
    category,
    age_grade,
    series_id,
    sub_category,
    authors,
    service_property,
    operator_property,
    badge,
    is_waitfree,
    category_uid,
    waitfree_period_by_minute,
    business_model,
  }: ItemSeriesDto
): NormalListViewItem {
  const isHelixGift = !!service_property?.helix_gift;
  const todayGift =
    service_property?.today_gift &&
    createTodayGift(service_property.today_gift);
  const helixTodayGift =
    service_property?.helix_gift &&
    createHelixTodayGift(service_property.helix_gift);
  const benefitInfoText = getBenefitInfoText(
    isHelixGift ? service_property?.helix_gift : service_property?.today_gift
  );
  const {badgeList, ageGradeBadge, statusBadge, statusLabelText} =
    getCommonBadge({
      itemType: ItemType.NormalListView,
      badge,
      age_grade,
      is_waitfree,
      waitfree_period_by_minute: waitfree_period_by_minute || null,
      business_model,
      self_censorship: operator_property?.self_censorship,
      is_manga: operator_property?.is_manga,
    });

  const altText = getItemAltText({
    title,
    badgeList,
    statusBadge,
    ageGradeBadge,
    subtitleList: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: category,
      subCategoryName:
        category !== sub_category
          ? removeCategoryName(sub_category, category_uid)
          : undefined,
      authors,
      servicePropertyDto: undefined,
      isAltText: true,
    }),
    // 대여권 n장 선물
    operationalTextList: benefitInfoText
      ? [
          t('library_tab_accessibility_my_gift_info', {
            values: {value: benefitInfoText},
          }),
        ]
      : undefined,
    ticketExpiredText: getTicketExpiredText(
      service_property?.today_gift?.ticket_expire_at
    ),
  });

  return {
    id: `NormalList-withTodayGiftItemSeriesDto-${series_id}`,
    altText,
    // TODO: helix_gift_item_uid 값이 today_gift_uid 값과 1:1 매핑되는 값이 맞는지 확인 필요
    ticketUid: isHelixGift
      ? service_property?.helix_gift?.helix_gift_uid
      : service_property?.today_gift?.today_gift_uid,
    isChecked: false,
    isCheckMode: false,
    showPlayerIcon: false,
    isReceived: isHelixGift
      ? service_property?.helix_gift?.is_received
      : service_property?.today_gift?.is_received,
    isHelixGift,
    type: ItemType.NormalListView,
    badgeList,
    ageGradeBadge,
    statusBadge,
    statusLabelText,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    row1: title,
    row2: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: category,
      subCategoryName:
        category !== sub_category
          ? removeCategoryName(sub_category, category_uid)
          : undefined,
      servicePropertyDto: undefined,
      authors,
    }),
    row4: benefitInfoText ? [benefitInfoText] : undefined,
    row5: getTicketExpiredText(
      isHelixGift
        ? service_property?.helix_gift?.ticket_expire_at
        : service_property?.today_gift?.ticket_expire_at
    ), // TODO: 선물 이용권 로직 추가
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    eventLog: {
      eventMeta: {
        id: String(series_id),
        type: 'seriesId',
        name: title,
        category,
        subcategory: sub_category,
        provider: isHelixGift ? 'helix' : '서비스',
        series: title,
        series_id: String(series_id),
      },
    },
    giftEventLog: {
      customProps: {
        content_cnt: String(
          isHelixGift ? helixTodayGift?.ticketCount : todayGift?.ticketCount
        ),
        event_series_id: String(series_id),
        event_ticket_type: `${
          (isHelixGift ? helixTodayGift?.ticketKind : todayGift?.ticketKind) ===
          TicketKind.Own
            ? '소장'
            : '대여'
        }_선물이용권`,
        ...(isHelixGift && {
          helix_yn: 'y',
          helix_id: String(service_property.helix_gift?.helix_gift_uid),
        }),
      },
    },
    seriesId: series_id,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

function createCommonInvenItem(
  userInfo: Maybe<User>,
  {
    thumbnail,
    title,
    category,
    sub_category,
    age_grade,
    authors,
    series_id,
    category_uid,
    badge,
    is_waitfree,
    waitfree_period_by_minute,
    operator_property,
    business_model,
  }: ItemSeriesDto
): NormalListViewItem {
  const {badgeList, ageGradeBadge, statusBadge, statusLabelText} =
    getCommonBadge({
      itemType: ItemType.NormalListView,
      badge,
      age_grade,
      is_waitfree,
      waitfree_period_by_minute: waitfree_period_by_minute || null,
      business_model,
      self_censorship: operator_property?.self_censorship,
      is_manga: operator_property?.is_manga,
    });
  return {
    id: `NormalList-commonInvenItem-${series_id}`,
    isChecked: false,
    isCheckMode: false,
    showPlayerIcon: false,
    type: ItemType.NormalListView,
    badgeList,
    ageGradeBadge,
    statusBadge,
    statusLabelText,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    row1: title,
    row2: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: category,
      subCategoryName:
        category !== sub_category
          ? removeCategoryName(sub_category, category_uid)
          : undefined,
      servicePropertyDto: undefined,
      authors,
    }),
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    eventLog: {
      eventMeta: {
        id: String(series_id),
        type: 'seriesId',
        name: title,
        category,
        subcategory: sub_category,
        provider: '서비스',
        series: title,
        series_id: String(series_id),
      },
    },
    seriesId: series_id,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

function withInvenLikeSeriesDto(
  userInfo: Maybe<User>,
  itemSeries: ItemSeriesDto
): NormalListViewItem {
  const commonInvenItemInfo = createCommonInvenItem(userInfo, itemSeries);

  // 작품, {작품명}, {BM뱃지}, {연령 뱃지}, {최신 회차 업데이트됨}, {대분류}, {중분류}, 작가 {n}, 조회수 {n}, {연재정보}, {무료 편수 정보}, 버튼
  const altText = getItemAltText({
    title: commonInvenItemInfo.row1 || '',
    badgeList: commonInvenItemInfo.badgeList,
    ageGradeBadge: commonInvenItemInfo.ageGradeBadge || undefined,
    statusBadge: commonInvenItemInfo.statusBadge || undefined,
    // createCommonInvenItem에서 row2를 만드는것과 동일하게 subtitle을 넘겨줍니다.
    subtitleList: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: itemSeries.category,
      subCategoryName:
        itemSeries.category !== itemSeries.sub_category
          ? removeCategoryName(itemSeries.sub_category, itemSeries.category_uid)
          : undefined,
      authors: itemSeries.authors,
      servicePropertyDto: undefined,
      isAltText: true,
    }),
    normalListViewMetaList: getNormalListViewMetaList({
      viewCount: itemSeries.service_property?.view_count || 0,
      onIssueVo: itemSeries.on_issue,
      isAltText: true,
    }),
    operationalTextList:
      itemSeries.state && createSaleState(itemSeries.state) === SaleState.Stop
        ? []
        : itemSeries.free_slide_count
          ? [
              t('library_tab_like_free_count', {
                values: {value: itemSeries.free_slide_count},
              }),
            ]
          : undefined,
  });

  return {
    ...commonInvenItemInfo,
    id: `NormalList-InvenLikeSeriesDto-${itemSeries.series_id}`,
    altText,
    isAlaramOn: !!itemSeries.service_property?.alarm_on,
    row3: {
      id: uuidv4(),
      metaList: getNormalListViewMetaList({
        viewCount: itemSeries.service_property?.view_count || 0,
        onIssueVo: itemSeries.on_issue,
      }),
    },
    row4:
      itemSeries.state && createSaleState(itemSeries.state) === SaleState.Stop
        ? []
        : itemSeries.free_slide_count
          ? [
              t('library_tab_like_free_count', {
                values: {value: itemSeries.free_slide_count},
              }),
            ]
          : undefined,
    eventLog: {
      ...commonInvenItemInfo.eventLog,
      click: {
        copy: itemSeries.free_slide_count
          ? t('library_tab_like_free_count', {
              values: {value: itemSeries.free_slide_count},
            })
          : undefined,
      },
    },
  };
}
function withInvenPaidSeriesDto(
  userInfo: Maybe<User>,
  itemSeries: ItemSeriesDto
): NormalListViewItem {
  const commonInvenItemInfo = createCommonInvenItem(userInfo, itemSeries);

  const ticketDescriptionList = [
    ...(itemSeries.service_property?.rental_ticket_count
      ? [
          t('common_ticket_rental_amount', {
            values: {
              value: formatWithCommas(
                itemSeries.service_property.rental_ticket_count
              ),
            },
          }),
        ]
      : []),
    ...(itemSeries.service_property?.own_ticket_count
      ? [
          t('common_ticket_possession_amount', {
            values: {
              value: formatWithCommas(
                itemSeries.service_property.own_ticket_count
              ),
            },
          }),
        ]
      : []),
  ];

  // 작품, {작품명}, {BM뱃지}, {연령 뱃지}, {최신 회차 업데이트됨}, {대분류}, {중분류}, 작가 {}, {연재 정보}, {구매 일자}, 버튼
  const altText = getItemAltText({
    title: commonInvenItemInfo.row1 || '',
    badgeList: commonInvenItemInfo.badgeList,
    ageGradeBadge: commonInvenItemInfo.ageGradeBadge || undefined,
    statusBadge: commonInvenItemInfo.statusBadge || undefined,
    subtitleList: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: itemSeries.category,
      subCategoryName:
        itemSeries.category !== itemSeries.sub_category
          ? removeCategoryName(itemSeries.sub_category, itemSeries.category_uid)
          : undefined,
      authors: itemSeries.authors,
      servicePropertyDto: undefined,
      isAltText: true,
    }),
    normalListViewMetaList: getNormalListViewMetaList({
      onIssueVo: itemSeries.on_issue,
      isAltText: true,
    }),
    operationalTextList: [
      ...(itemSeries.service_property?.purchased_dt
        ? [
            t('common_accessibility_purchased_date', {
              values: {
                value: timeago({
                  date: parseDate(itemSeries.service_property.purchased_dt),
                }),
              },
            }),
          ]
        : []),
      ...ticketDescriptionList,
    ],
  });

  return {
    ...commonInvenItemInfo,
    id: `NormalList-InvenPaidSeriesDto-${itemSeries.series_id}`,
    altText,
    row3: {
      id: uuidv4(),
      metaList: getNormalListViewMetaList({onIssueVo: itemSeries.on_issue}),
    },
    row4: [
      ...(itemSeries.service_property?.purchased_dt
        ? [
            timeago({
              date: parseDate(itemSeries.service_property.purchased_dt),
            }),
          ]
        : []),
      ...ticketDescriptionList,
    ],
    eventLog: {
      ...commonInvenItemInfo.eventLog,
      click: {
        copy:
          ticketDescriptionList && ticketDescriptionList.length > 0
            ? ticketDescriptionList.join(', ')
            : undefined,
      },
    },
  };
}

export function withInvenRecentSeriesDto(
  userInfo: Maybe<User>,
  itemSeries: ItemSeriesDto
): NormalListViewItem {
  const commonInvenItemInfo = createCommonInvenItem(userInfo, itemSeries);
  const continueData = itemSeries?.service_property?.page_continue
    ? createContinueInfo(
        itemSeries?.service_property?.page_continue,
        itemSeries.series_id
      )
    : undefined;
  const row4 =
    itemSeries.state && createSaleState(itemSeries.state) === SaleState.Stop
      ? []
      : continueData?.hasNewSingle
        ? [t('library_content_updated')]
        : itemSeries.service_property?.waitfree_ticket?.charged_count &&
            itemSeries.service_property.waitfree_ticket.charged_count > 0
          ? [t('library_content_wuf_charged')]
          : itemSeries.service_property?.today_gift?.ticket_count &&
              itemSeries.service_property?.today_gift?.ticket_count > 0
            ? [
                t('library_tab_content_gift_ticket_count', {
                  values: {
                    value: itemSeries.service_property.today_gift.ticket_count,
                  },
                }),
              ]
            : continueData?.hasUnreadSingle
              ? [t('library_tab_unread')]
              : [];

  // 작품, {작품명}, {BM뱃지}, {연령 뱃지}, {최신 회차 업데이트됨}, {대분류}, {중분류}, 작가 {}, {열람 일자}, {연재 정보}, {혜택정보 문구}, 버튼
  const altText = getItemAltText({
    title: commonInvenItemInfo.row1 || '',
    badgeList: commonInvenItemInfo.badgeList,
    ageGradeBadge: commonInvenItemInfo.ageGradeBadge || undefined,
    statusBadge: commonInvenItemInfo.statusBadge || undefined,
    subtitleList: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: itemSeries.category,
      subCategoryName:
        itemSeries.category !== itemSeries.sub_category
          ? removeCategoryName(itemSeries.sub_category, itemSeries.category_uid)
          : undefined,
      authors: itemSeries.authors,
      servicePropertyDto: undefined,
      isAltText: true,
    }),
    normalListViewMetaList: getNormalListViewMetaList({
      lastReadDt: itemSeries.service_property?.page_continue?.last_read_dt,
      onIssueVo: itemSeries.on_issue,
      isAltText: true,
    }),
    operationalTextList: row4,
  });

  return {
    ...commonInvenItemInfo,
    id: `NormalList-InvenRecentSeriesDto-${itemSeries.series_id}`,
    altText,
    row3: {
      id: uuidv4(),
      metaList: getNormalListViewMetaList({
        lastReadDt: itemSeries.service_property?.page_continue?.last_read_dt,
        onIssueVo: itemSeries.on_issue,
      }),
    },
    row4,
    continueData,
    continueScheme:
      !continueData ||
      continueData?.continueProductType === ContinueProductType.Next
        ? undefined
        : continueData?.scheme,
    nextProductScheme:
      continueData &&
      continueData.continueProductType === ContinueProductType.Next
        ? continueData?.scheme
        : undefined,
    eventLog: {
      ...commonInvenItemInfo.eventLog,
      click: {
        copy: row4.length > 0 ? row4[0] : undefined,
      },
    },
  };
}

function withSearchThemeKeywordItemSeriesDto(
  userInfo: Maybe<User>,
  {
    title,
    thumbnail,
    category,
    series_id,
    category_uid,
    sub_category,
    authors,
    last_slide_added_dt,
    on_issue,
    service_property,
    age_grade,
    badge,
    is_waitfree,
    waitfree_period_by_minute,
    business_model,
    operator_property,
  }: ItemSeriesDto
): NormalListViewItem {
  const currentDate = new Date();
  const {title: operatorTitle, self_censorship} = operator_property || {};
  const {badgeList, ageGradeBadge, statusBadge, statusLabelText} =
    getCommonBadge({
      itemType: ItemType.NormalListView,
      badge,
      age_grade,
      is_waitfree,
      waitfree_period_by_minute: waitfree_period_by_minute || null,
      business_model,
      self_censorship,
      is_manga: operator_property?.is_manga,
    });
  return {
    id: `NormalList-SearchThemeKeywordItemSeriesDto-${series_id}`,
    isChecked: false,
    isCheckMode: false,
    showPlayerIcon: false,
    type: ItemType.NormalListView,
    badgeList,
    ageGradeBadge,
    statusBadge,
    statusLabelText,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: self_censorship,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    row1: operatorTitle || title, // 동일작의 경우 운영툴에서 설정한 타이틀을 우선으로 보여줍니다
    // 카테고리는 전체탭이외의 탭 즉, 웹툰, 웹소설의 경우 대분류를 표기하지 않는다.
    // 기획서 : https://podotree.atlassian.net/wiki/spaces/SVC/pages/2674198583/WEB#4.-%EC%9D%B8%EA%B8%B0-%EA%B2%80%EC%83%89%EC%96%B4
    row2: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: category,
      subCategoryName:
        category !== sub_category
          ? removeCategoryName(sub_category, category_uid)
          : undefined,
      servicePropertyDto: undefined,
      authors,
    }),
    row3: {
      id: uuidv4(),
      metaList: getNormalListViewMetaList({
        viewCount: service_property?.view_count,
        lastSlideAddedDt: last_slide_added_dt,
        onIssueVo: on_issue,
        currentDate,
        needShowFullUpdatedDate: true,
      }),
    },
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    eventLog: {
      eventMeta: {
        id: String(series_id),
        type: 'seriesId',
        name: title,
        category,
        subcategory: sub_category,
        provider: '서비스',
        series: title,
        series_id: String(series_id),
      },
    },
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

function withSearchCommonItemSeriesDto(
  userInfo: Maybe<User>,
  {
    title,
    thumbnail,
    category,
    series_id,
    sub_category,
    authors,
    last_slide_added_dt,
    on_issue,
    service_property,
    age_grade,
    badge,
    is_waitfree,
    waitfree_period_by_minute,
    category_uid,
    business_model,
    operator_property,
  }: ItemSeriesDto,
  shouldShowCategory: boolean
): NormalListViewItem {
  const currentDate = new Date();
  const {title: operatorTitle, self_censorship} = operator_property || {};
  const {badgeList, ageGradeBadge, statusBadge, statusLabelText} =
    getCommonBadge({
      itemType: ItemType.NormalListView,
      badge,
      age_grade,
      is_waitfree,
      waitfree_period_by_minute: waitfree_period_by_minute || null,
      business_model,
      self_censorship,
      is_manga: operator_property?.is_manga,
    });

  const altText = getItemAltText({
    title,
    badgeList,
    statusBadge,
    ageGradeBadge,
    subtitleList: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: shouldShowCategory ? category : undefined,
      subCategoryName:
        category !== sub_category
          ? removeCategoryName(sub_category, category_uid)
          : undefined,
      authors,
      servicePropertyDto: undefined,
      isAltText: true,
    }),
    normalListViewMetaList: getNormalListViewMetaList({
      viewCount: service_property?.view_count,
      lastSlideAddedDt: last_slide_added_dt,
      onIssueVo: on_issue,
      currentDate,
      needShowFullUpdatedDate: true,
      isAltText: true,
    }),
  });

  return {
    id: `NormalList-SearchCommonItemSeriesDto-${series_id}`,
    altText,
    isChecked: false,
    isCheckMode: false,
    showPlayerIcon: false,
    type: ItemType.NormalListView,
    badgeList,
    ageGradeBadge,
    selfCensorship: self_censorship,
    statusBadge,
    statusLabelText,
    ageGrade: createAgeGrade(age_grade),
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    row1: operatorTitle || title, // 동일작의 경우 운영툴에서 설정한 타이틀을 우선으로 보여줍니다
    row2: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: shouldShowCategory ? category : undefined,
      subCategoryName:
        category !== sub_category
          ? removeCategoryName(sub_category, category_uid)
          : undefined,
      servicePropertyDto: undefined,
      authors,
    }),
    row3: {
      id: uuidv4(),
      metaList: getNormalListViewMetaList({
        viewCount: service_property?.view_count,
        lastSlideAddedDt: last_slide_added_dt,
        onIssueVo: on_issue,
        currentDate,
        needShowFullUpdatedDate: true,
      }),
    },
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    eventLog: {
      eventMeta: {
        id: String(series_id),
        type: 'seriesId',
        name: title,
        category,
        subcategory: sub_category,
        series: title,
        provider: '서비스',
        series_id: String(series_id),
      },
    },
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

function withListViewLandingItemSingleDto(
  userInfo: Maybe<User>,
  {
    title,
    thumbnail,
    slide_type,
    series_property,
    service_property,
    operator_property,
    product_id,
    age_grade,
    is_free,
  }: ItemSingleDto
): NormalListViewItem {
  const ageGradeVo = getAgeGradeVo({
    singleAgeGrade: age_grade,
    seriesAgeGrade: series_property?.age_grade,
  });
  const {
    category = '-',
    category_uid = CategoryTypeVo.None,
    sub_category = '-',
    series_id,
  } = series_property || {};
  const continueData = createContinueInfo(
    {
      first_read_is_free: is_free,
      first_read_product_id: product_id,
      first_read_product_title: title,
      has_new_single: service_property?.page_continue?.has_new_single ?? false,
      has_unread_single:
        service_property?.page_continue?.has_unread_single ?? false,
    },
    series_property?.series_id
  );
  return {
    id: `NormalList-ListViewLandingItemSingleDto-${product_id}`,
    isChecked: false,
    isCheckMode: false,
    showPlayerIcon: slide_type === SlideTypeVo.TalkHumor,
    type: ItemType.NormalListView,
    badgeList: [],
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    row1: title,
    // 다른 형태와 다른 예외적인 경우 이기 때문에 함수를 쓰지 않고 별도로 처리합니다.
    row2: [
      ...(series_property?.title ? [series_property.title] : []),
      ...(series_property?.authors ? [series_property.authors] : []),
    ],
    row3: {
      id: uuidv4(),
      metaList: [
        ...(service_property?.view_count !== undefined
          ? [getFormattedNumberWithSuffix(service_property.view_count)]
          : []),
        category,
        ...(category !== sub_category
          ? [removeCategoryName(sub_category, category_uid)]
          : []),
      ],
    },
    scheme: `kakaopage://open/viewer?product_id=${product_id}&series_id=${
      series_property?.series_id ?? ''
    }`,
    isSingle: true,
    productId: product_id,
    singleSlideType: slide_type ? createSlideType(slide_type) : undefined,
    ageGrade: createAgeGrade(ageGradeVo),
    selfCensorship: operator_property?.self_censorship,
    seriesId: series_property?.series_id,
    continueData,
    eventLog: {
      eventMeta: {
        id: String(product_id),
        type: 'productId',
        provider: '서비스',
        name: title,
        series: title,
        series_id: String(series_id),
        category,
        subcategory: sub_category,
      },
      customProps: {
        landing_path: 'viewer', // 작품홈 랜딩
        view_type: 'single_list_view',
      },
    },
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

function getBenefitInfoText(today_gift?: TodayGiftDto | HelixTodayGiftDto) {
  if (today_gift) {
    const {ticket_type, ticket_count} = today_gift;

    const ticketKind = createTicketKind(ticket_type);
    if (ticketKind === TicketKind.Own) {
      return t('common_ticket_possession_amount', {
        values: {
          value: formatWithCommas(ticket_count),
        },
      });
    }
    if (ticketKind === TicketKind.Rent) {
      return t('common_ticket_rental_amount', {
        values: {
          value: formatWithCommas(ticket_count),
        },
      });
    }
  }
}

function withSearchCommonItemSetDto(
  {
    type: _type,
    set_id,
    series_id,
    title,
    thumbnail,
    category,
    category_uid,
    sub_category,
    age_grade,
    authors,
    discount_rate,
    translate_property: _translate_property,
    operator_property,
  }: SearchSetItemDto,
  shouldShowCategory: boolean
): NormalListViewItem {
  const {ageGradeBadge} = getCommonBadge({
    itemType: ItemType.NormalListView,
    age_grade,
    is_waitfree: false,
    waitfree_period_by_minute: null,
    self_censorship: operator_property?.self_censorship,
  });

  const eventLog: EventLog = {
    eventMeta: {
      id: String(set_id),
      type: 'setId',
      provider: 'service',
      name: title,
      series_id: String(series_id),
      category,
      subcategory: sub_category,
    },
    customProps: {
      landing_path: 'sets',
    },
  };

  const row2 = getSubtitleList({
    itemType: ItemType.NormalListView,
    metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
    categoryName: shouldShowCategory ? category : undefined,
    subCategoryName:
      category !== sub_category
        ? removeCategoryName(sub_category, category_uid)
        : undefined,
    servicePropertyDto: undefined,
    authors,
  });

  const isSet = _type === 'SET';

  const altText = getItemAltText({
    title,
    ageGradeBadge,
    subtitleList: row2,
    isSet,
  });

  return {
    id: `NormalList-SearchCommonItemSetDto-${set_id}`,
    isChecked: false,
    isCheckMode: false,
    isSet,
    showPlayerIcon: false,
    type: ItemType.NormalListView,
    altText,
    badgeList: [],
    ageGradeBadge,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    row1: title,
    row2,
    scheme: `kakaopage://open/content/set_overview?set_id=${set_id}`,
    discountRate: discount_rate,
    discountRateText: discount_rate ? `${discount_rate}%` : undefined,
    eventLog,
  };
}

function withListViewLandingItemSetDto(
  userInfo: Maybe<User>,
  {
    title,
    set_id,
    thumbnail,
    category,
    series_id,
    sub_category,
    age_grade,
    authors,
    category_uid,
    price,
    discount_price,
    discount_rate,
    operator_property,
  }: ItemSetDto
): NormalListViewItem {
  const {ageGradeBadge} = getCommonBadge({
    itemType: ItemType.NormalListView,
    age_grade,
    self_censorship: operator_property?.self_censorship,
    is_waitfree: false,
    waitfree_period_by_minute: null,
  });

  const eventLog: EventLog = {
    eventMeta: {
      id: String(set_id),
      type: 'setId',
      provider: 'service',
      name: title,
      series_id: String(series_id),
      category,
      subcategory: sub_category,
    },
    customProps: {
      landing_path: 'sets',
    },
  };
  return {
    id: `NormalList-listViewLandingItemSetDto-${series_id}`,
    isChecked: false,
    isCheckMode: false,
    showPlayerIcon: false,
    type: ItemType.NormalListView,
    badgeList: [],
    ageGradeBadge,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    row1: title,
    row2: getSubtitleList({
      itemType: ItemType.NormalListView,
      metaInfoType: MetaInfoType.CategoryAndSubCategoryAndAuthorName,
      categoryName: category,
      subCategoryName:
        category !== sub_category
          ? removeCategoryName(sub_category, category_uid)
          : undefined,
      servicePropertyDto: undefined,
      authors,
    }),
    scheme: `kakaopage://open/content/set_overview?set_id=${set_id}`,
    price,
    discountPrice: discount_price,
    discountRate: discount_rate,
    discountRateText: discount_rate ? `${discount_rate}%` : undefined,
    eventLog,
  };
}
